import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getKoreanToday } from '../utils/dateUtils';

export const useReadingStreak = (userId) => {
  const [streakData, setStreakData] = useState({
    currentStreak: 0,
    totalReadingDays: 0,
    lastReadDate: null,
    readDates: {}
  });

  useEffect(() => {
    if (!userId) return;

    const fetchStreakData = async () => {
      const streakRef = doc(db, 'readingStreaks', userId);
      const streakDoc = await getDoc(streakRef);

      if (streakDoc.exists()) {
        setStreakData(streakDoc.data());
      } else {
        const initialData = {
          currentStreak: 0,
          totalReadingDays: 0,
          lastReadDate: null,
          readDates: {}
        };
        await setDoc(streakRef, initialData);
        setStreakData(initialData);
      }
    };

    fetchStreakData();
  }, [userId]);

  // 특정 날짜의 독서 기록이 있는지 확인하는 함수
  const checkDateHasReading = (books, date) => {
    return books.some(book => book.readingHistory?.[date] > 0);
  };

  // 연속 독서일 계산 함수 수정
  const calculateCurrentStreak = (readDates) => {
    const today = getKoreanToday();
    let streak = 0;
    let currentDate = new Date(today);

    // 오늘 읽었는지 확인
    if (!readDates[today]) {
      // 오늘 안 읽었으면 어제부터 확인
      currentDate.setDate(currentDate.getDate() - 1);
      const yesterday = currentDate.toISOString().split('T')[0];
      if (!readDates[yesterday]) {
        return 0; // 어제도 안 읽었으면 스트릭 0
      }
    }

    // 연속된 날짜 계산
    while (true) {
      const dateStr = currentDate.toISOString().split('T')[0];
      if (!readDates[dateStr]) break;
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    }

    return streak;
  };

  // 스트릭 업데이트 함수 수정
  const updateStreak = async (date = getKoreanToday(), books = [], completedBooks = []) => {
    if (!userId) {
      console.log('updateStreak: userId가 없음');
      return;
    }

    console.log('updateStreak 시작:', {
      date,
      booksCount: books.length,
      currentStreakData: streakData
    });

    const streakRef = doc(db, 'readingStreaks', userId);
    const today = date;
    let newStreakData = { ...streakData };
    
    const hasReading = checkDateHasReading(books, today);
    console.log('독서 기록 확인:', {
      today,
      hasReading,
      booksReadingHistory: books.map(book => ({
        id: book.id,
        todayReading: book.readingHistory?.[today]
      }))
    });
    
    if (hasReading) {
      // 기존 readDates 복사
      const allReadDates = { ...newStreakData.readDates };
      // 오늘 날짜 추가
      allReadDates[today] = true;

      // 모든 책의 모든 독서 기록 날짜 추가 (현재 읽는 책 + 완독한 책)
      const allBooks = [...books, ...completedBooks];
      allBooks.forEach(book => {
        if (book.readingHistory) {
          Object.keys(book.readingHistory).forEach(date => {
            if (book.readingHistory[date] > 0) {
              allReadDates[date] = true;
            }
          });
        }
      });

      newStreakData.readDates = allReadDates;
      newStreakData.totalReadingDays = Object.keys(allReadDates).length;
      
      console.log('스트릭 데이터 업데이트:', {
        readDates: Object.keys(allReadDates).sort(),
        totalReadingDays: newStreakData.totalReadingDays
      });

      // 마지막 독서일 업데이트
      const sortedDates = Object.keys(allReadDates).sort();
      newStreakData.lastReadDate = sortedDates[sortedDates.length - 1];

      // 현재 연속 독서일 계산
      newStreakData.currentStreak = calculateCurrentStreak(allReadDates);

      try {
        await updateDoc(streakRef, newStreakData);
        setStreakData(newStreakData);
        console.log('Firestore 업데이트 성공:', newStreakData);
      } catch (error) {
        console.error('Firestore 업데이트 실패:', error);
      }
    } else {
      console.log('오늘 독서 기록 없음');
    }
  };

  // 책 삭제 시 호출할 함수 수정
  const recalculateStreak = async (books) => {
    if (!userId) return;

    console.log('recalculateStreak 호출됨:', {
      currentReadDates: streakData.readDates,
      booksCount: books.length,
      booksReadingHistory: books.map(book => ({
        title: book.title,
        readingDates: Object.keys(book.readingHistory || {})
      }))
    });

    const streakRef = doc(db, 'readingStreaks', userId);
    let newStreakData = { ...streakData };
    let newReadDates = { ...newStreakData.readDates };

    // 삭제된 책을 제외한 나머지 책들의 독서 기록 확인
    Object.keys(newReadDates).forEach(date => {
      const hasReading = checkDateHasReading(books, date);
      console.log(`날짜 ${date} 독서 기록 확인:`, hasReading);
      
      if (!hasReading) {
        delete newReadDates[date];
      }
    });

    newStreakData.readDates = newReadDates;
    newStreakData.totalReadingDays = Object.keys(newReadDates).length;
    
    const sortedDates = Object.keys(newReadDates).sort();
    newStreakData.lastReadDate = sortedDates[sortedDates.length - 1] || null;
    newStreakData.currentStreak = calculateCurrentStreak(newReadDates);

    console.log('재계산된 스트릭 데이터:', {
      readDates: Object.keys(newReadDates),
      totalReadingDays: newStreakData.totalReadingDays,
      currentStreak: newStreakData.currentStreak,
      lastReadDate: newStreakData.lastReadDate
    });

    await updateDoc(streakRef, newStreakData);
    setStreakData(newStreakData);
  };

  return {
    ...streakData,
    updateStreak,
    recalculateStreak
  };
}; 